import React, { useMemo } from 'react';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi2';
import { Button } from '../../ui';

const Pagination = ({
  pageIndex,
  pageOptions,
  gotoPage,
  previousPage,
  canPreviousPage,
  nextPage,
  canNextPage,
}) => {
  const totalPages = pageOptions.length;
  const currentPage = pageIndex + 1;

  const pages = useMemo(() => {
    const items = [];
    const delta = 2; // Number of pages either side of current page
    let start = Math.max(2, currentPage - delta);
    let end = Math.min(totalPages - 1, currentPage + delta);

    // Adjust to show more pages if near the start or end
    if (currentPage <= delta + 1) {
      end = Math.min(totalPages - 1, delta * 2 + 1);
    }
    if (currentPage >= totalPages - delta) {
      start = Math.max(2, totalPages - delta * 2);
    }

    // Always include first page
    items.push(1);

    if (start > 2) {
      items.push('...');
    }

    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }

    if (end < totalPages - 1) {
      items.push('...');
    }

    // Always include last page if there's more than one page
    if (totalPages > 1) {
      items.push(totalPages);
    }

    return items;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className='flex flex-col sm:flex-row items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800 rounded-b-xl gap-4'>
      <div className='flex items-center gap-2 order-2 sm:order-1'>
        <span className='text-sm text-gray-500 dark:text-gray-400'>
          Showing <span className='font-bold text-gray-900 dark:text-white'>Page {currentPage}</span> of <span className='font-bold text-gray-900 dark:text-white'>{totalPages}</span>
        </span>
      </div>
      
      <div className='flex items-center justify-end gap-1.5 order-1 sm:order-2 w-full sm:w-auto'>
        <Button
          variant='ghost'
          size='sm'
          onClick={previousPage}
          disabled={!canPreviousPage}
          className='!px-2 !min-w-[40px] !rounded-xl'
          title='Previous Page'
        >
          <HiOutlineChevronLeft className='w-4 h-4' />
        </Button>

        <div className='flex items-center gap-1 overflow-hidden h-10'>
          {pages.map((page, index) => {
            const isEllipsis = page === '...';
            const isActive = currentPage === page;

            if (isEllipsis) {
              return (
                <span 
                  // eslint-disable-next-line react/no-array-index-key
                  key={`ellipsis-${index}`} 
                  className='w-8 h-8 flex items-center justify-center text-gray-400'
                >
                  {page}
                </span>
              );
            }

            return (
              <button
                key={`page-${page}`}
                type='button'
                onClick={() => gotoPage(page - 1)}
                className={`
                  w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold transition-all duration-200
                  ${isActive ?
                    'bg-primary-500 text-white shadow-lg shadow-primary-500/30 -translate-y-0.5 scale-110'
                    : 'text-gray-500 hover:bg-white dark:hover:bg-gray-800 hover:text-primary-500 hover:shadow-sm'
                  }
                `}
              >
                {page}
              </button>
            );
          })}
        </div>

        <Button
          variant='ghost'
          size='sm'
          onClick={nextPage}
          disabled={!canNextPage}
          className='!px-2 !min-w-[40px] !rounded-xl'
          title='Next Page'
        >
          <HiOutlineChevronRight className='w-4 h-4' />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
